import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect, useRef } from 'react';
import projectsService from '../services/projectsService';
import notificationsService from '../services/notificationsService';
import { MetadataPrecheckReusePayload, PendingAttachment } from '../types';

type ConversionStatus = 'idle' | 'converting' | 'done' | 'error';

interface ConversionState {
    status: ConversionStatus;
    error?: string;
    file?: File;
    timestamp: number;
    tempFileId?: string;
    metadataValidationExecuted?: boolean;
    metadataValidationSkipReason?: string | null;
    metadataValidationPending?: boolean;
    extractedFields?: { status?: string; nd_doc_ref?: string; revision?: string; confidence?: number; source?: string } | null;
    validationWarnings?: Array<{ field: string; expected: string; detected: string; severity: string; reason: string }>;
    progressMessage?: string;
    health?: 'running' | 'slow' | 'stalled';
    healthMessage?: string;
    workerName?: string;
}

interface ConversionMeta {
    projectId?: number;
    documentId?: number;
    revisionId?: number;
}

export interface UploadDraft {
    mainFile: File | null;
    attachments: PendingAttachment[];
    archives: File[];
    nativeFiles: File[];
    crsFiles: File[];
    comment: string;
    checkedConversion: boolean;
    metadataPrecheckReuse: MetadataPrecheckReusePayload | null;
}

const EMPTY_DRAFT: UploadDraft = {
    mainFile: null,
    attachments: [],
    archives: [],
    nativeFiles: [],
    crsFiles: [],
    comment: '',
    checkedConversion: false,
    metadataPrecheckReuse: null,
};

function draftHasContent(draft: UploadDraft | undefined): boolean {
    if (!draft) return false;
    return Boolean(
        draft.mainFile ||
        draft.attachments.length > 0 ||
        draft.archives.length > 0 ||
        draft.nativeFiles.length > 0 ||
        draft.crsFiles.length > 0 ||
        draft.comment.trim().length > 0
    );
}

function conversionHasPendingWork(conversion: ConversionState | undefined): boolean {
    if (!conversion) return false;
    return conversion.status === 'converting' || conversion.status === 'done' || conversion.status === 'error';
}

interface UploadContextType {
    conversions: Record<string, ConversionState>;
    startConversion: (id: string, file: File, meta?: ConversionMeta) => Promise<void>;
    getConversion: (id: string) => ConversionState | undefined;
    clearConversion: (id: string) => void;
    getDraft: (id: string) => UploadDraft;
    setDraft: (id: string, draft: Partial<UploadDraft>) => void;
    clearDraft: (id: string) => void;
    hasDraft: (id: string) => boolean;
    hasAnyDraft: () => boolean;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export const useUpload = () => {
    const context = useContext(UploadContext);
    if (!context) {
        throw new Error('useUpload must be used within an UploadProvider');
    }
    return context;
};

interface UploadProviderProps {
    children: ReactNode;
}

export const UploadProvider: React.FC<UploadProviderProps> = ({ children }) => {
    const [conversions, setConversions] = useState<Record<string, ConversionState>>({});
    const [drafts, setDrafts] = useState<Record<string, UploadDraft>>({});
    const conversionsRef = useRef<Record<string, ConversionState>>({});
    const draftsRef = useRef<Record<string, UploadDraft>>({});

    const updateConversions = useCallback((updater: (prev: Record<string, ConversionState>) => Record<string, ConversionState>) => {
        const next = updater(conversionsRef.current);
        conversionsRef.current = next;
        setConversions(next);
    }, []);

    const updateDrafts = useCallback((updater: (prev: Record<string, UploadDraft>) => Record<string, UploadDraft>) => {
        const next = updater(draftsRef.current);
        draftsRef.current = next;
        setDrafts(next);
    }, []);

    const startConversion = useCallback(async (id: string, file: File, meta?: ConversionMeta) => {
        const startedAt = Date.now();
        updateConversions(prev => ({
            ...prev,
            [id]: {
                status: 'converting',
                file: file,
                timestamp: startedAt
            }
        }));

        try {
            const {
                tempFileId,
                metadataValidationExecuted,
                metadataValidationSkipReason,
                extractedFields,
                validationWarnings
            } = await projectsService.convertPreview(file, {
                documentId: meta?.documentId,
                revisionId: meta?.revisionId,
                onStatus: (progress) => {
                    updateConversions(prev => {
                        const current = prev[id];
                        if (!current || current.timestamp !== startedAt || current.status !== 'converting') {
                            return prev;
                        }
                        return {
                            ...prev,
                            [id]: {
                                ...current,
                                progressMessage: progress.progressMessage || undefined,
                                health: progress.health || undefined,
                                healthMessage: progress.healthMessage || undefined,
                                workerName: progress.workerName || undefined,
                            },
                        };
                    });
                },
            });

            let applied = false;
            updateConversions(prev => {
                if (prev[id]?.timestamp !== startedAt) {
                    return prev;
                }
                applied = true;
                return {
                    ...prev,
                    [id]: {
                        ...prev[id],
                        status: 'done',
                        tempFileId: tempFileId || undefined,
                        metadataValidationExecuted: !!metadataValidationExecuted,
                        metadataValidationSkipReason: metadataValidationSkipReason || null,
                        metadataValidationPending: false,
                        extractedFields: extractedFields || null,
                        validationWarnings: validationWarnings || [],
                        error: undefined,
                    }
                };
            });

            if (applied && meta?.documentId) {
                try {
                    await notificationsService.createNotification({
                        message: "Document preview is ready. You can now complete your upload.",
                        type: 'document_preview_ready',
                        related_id: meta.documentId,
                        revision_id: meta.revisionId,
                        project_id: meta.projectId,
                    });
                } catch (e) {
                    console.warn('Failed to send completion notification', e);
                }
            }

        } catch (err: any) {
            console.error("Conversion failed in background", err);
            let errorMsg: string =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                err?.message ||
                "Failed to prepare document preview.";

            try {
                const data = err?.response?.data;
                if (data && typeof (data as any).text === 'function') {
                    const txt = await (data as Blob).text();
                    if (txt) {
                        try {
                            const parsed = JSON.parse(txt);
                            errorMsg = parsed?.error || parsed?.message || errorMsg;
                        } catch {
                            // Not JSON; keep existing message.
                        }
                    }
                }
            } catch {
                // Ignore parsing issues; keep existing message.
            }

            let applied = false;
            updateConversions(prev => {
                if (prev[id]?.timestamp !== startedAt) {
                    return prev;
                }
                applied = true;
                return {
                    ...prev,
                    [id]: {
                        ...prev[id],
                        status: 'error',
                        error: errorMsg,
                    }
                };
            });
            if (applied && meta?.documentId) {
                try {
                    await notificationsService.createNotification({
                        message: `Document preview failed: ${errorMsg}`,
                        type: 'document_preview_failed',
                        related_id: meta.documentId,
                        revision_id: meta.revisionId,
                        project_id: meta.projectId,
                    });
                } catch (e) {
                    console.warn('Failed to send failure notification', e);
                }
            }
        }
    }, [updateConversions]);

    const getConversion = useCallback((id: string) => {
        return conversionsRef.current[id];
    }, [conversions]);

    const clearConversion = useCallback((id: string) => {
        const current = conversionsRef.current[id];
        if (current?.status === 'done' && current?.tempFileId) {
            try {
                fetch(`/api/projects/convert-preview/cleanup/materialized/${current.tempFileId}`, {
                    method: 'POST',
                    keepalive: true,
                }).catch(() => { /* best effort */ });
            } catch {
                // Ignore: cleanup is best-effort.
            }
        }
        updateConversions(prev => {
            const newState = { ...prev };
            delete newState[id];
            return newState;
        });
    }, [updateConversions]);

    const getDraft = useCallback((id: string): UploadDraft => {
        return draftsRef.current[id] ?? EMPTY_DRAFT;
    }, [drafts]);

    const setDraft = useCallback((id: string, draft: Partial<UploadDraft>) => {
        updateDrafts(prev => ({
            ...prev,
            [id]: { ...(prev[id] ?? EMPTY_DRAFT), ...draft },
        }));
    }, [updateDrafts]);

    const clearDraft = useCallback((id: string) => {
        clearConversion(id);
        updateDrafts(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    }, [clearConversion, updateDrafts]);

    const hasDraft = useCallback((id: string) => {
        const draft = draftsRef.current[id];
        const conversion = conversionsRef.current[id];
        if (!draft && !conversion) return false;
        if (conversionHasPendingWork(conversion)) {
            return true;
        }
        return draftHasContent(draft);
    }, [drafts, conversions]);

    const hasAnyDraft = useCallback(() => {
        const draftIds = new Set([
            ...Object.keys(draftsRef.current),
            ...Object.keys(conversionsRef.current),
        ]);
        for (const id of draftIds) {
            if (hasDraft(id)) {
                return true;
            }
        }
        return false;
    }, [hasDraft]);

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasAnyDraft()) {
                e.preventDefault();
                e.returnValue = 'You have unsaved upload files. They will be lost if you refresh or leave this page.';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasAnyDraft]);

    return (
        <UploadContext.Provider value={{
            conversions,
            startConversion,
            getConversion,
            clearConversion,
            getDraft,
            setDraft,
            clearDraft,
            hasDraft,
            hasAnyDraft,
        }}>
            {children}
        </UploadContext.Provider>
    );
};
